import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamsApi } from '@/utils/api';
import { toast } from 'sonner';
import { aggregateRYG, RYG_META } from '@/utils/ryg';
import { relTime } from '@/utils/time';
import { Users, Crown, Pin, FileText, CheckSquare, AlertTriangle, ChevronDown, Building2, MoreVertical, Check, Search, CornerDownLeft, Plus, Trash2, Loader2, RotateCcw } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import InviteOnCreateDialog from '@/components/InviteOnCreateDialog';

const AV_COLORS = ['blue', 'green', 'ink', 'butter', 'sky'];
const initials = (s) => (s || '?').trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
const teamRYG = (t) => aggregateRYG({ red: t.overdue_tasks || 0, yellow: (t.open_tasks || 0) - (t.overdue_tasks || 0) });
const CAP = 8;

const ROLE_COPY = {
  owner: { label: 'Owner view', sub: 'Every workspace across your company' },
  lead: { label: 'Team lead view', sub: 'Workspaces for the teams you manage' },
  individual: { label: 'Your workspaces', sub: 'Workspaces you belong to' },
};

export default function WorkspaceTiles() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [role, setRole] = useState('individual');
  const [orgTeams, setOrgTeams] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('all'); // all | pinned | mine
  const [collapsed, setCollapsed] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [pinBusy, setPinBusy] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteBusy, setInviteBusy] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newWsName, setNewWsName] = useState('');
  const [wsBusy, setWsBusy] = useState(false);
  const [inviteTeam, setInviteTeam] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteName, setDeleteName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [orderIds, setOrderIds] = useState([]);
  const [dragWsId, setDragWsId] = useState(null);
  const [dropWsId, setDropWsId] = useState(null);

  const restoreWorkspace = async (id) => {
    setRestoring(id);
    try {
      const r = await teamsApi.restore(id);
      toast.success(`Workspace “${r.data.name || 'workspace'}” restored`);
      setTrash(prev => prev.filter(x => x.id !== id));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore workspace'); }
    finally { setRestoring(null); }
  };

  const deleteWorkspace = async () => {
    if (!deleteTarget || deleteName.trim() !== deleteTarget.name) return;
    const t = deleteTarget;
    setDeleting(true);
    try {
      await teamsApi.remove(t.id, deleteName.trim());
      toast.success(`Workspace “${t.name}” moved to Trash`, {
        description: 'You can restore it for 30 days.',
        action: { label: 'Undo', onClick: () => restoreWorkspace(t.id) },
      });
      setDeleteTarget(null); setDeleteName('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete workspace'); }
    finally { setDeleting(false); }
  };

  const createWorkspace = async (name) => {
    const clean = name.trim();
    if (!clean) { setCreating(false); setNewWsName(''); return; }
    setWsBusy(true);
    try {
      const res = await teamsApi.create(clean);
      toast.success(`Workspace “${clean}” created`);
      setCreating(false); setNewWsName('');
      setInviteTeam({ id: res.data.id, name: clean });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create workspace'); }
    finally { setWsBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [r, inv, tr] = await Promise.all([teamsApi.list(), teamsApi.invitations().catch(() => ({ data: {} })), teamsApi.trash().catch(() => ({ data: {} }))]);
      setTeams(r.data.teams || []);
      setRole(r.data.role || 'individual');
      setOrgTeams(r.data.org_teams || []);
      setRollup(r.data.company_rollup || null);
      setInvites(inv.data.invitations || []);
      setTrash(tr.data.workspaces || []);
      setOrderIds(r.data.workspace_order || []);
    } catch {
      // silent — Home still renders the personal panels
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respondInvite = async (teamId, accept) => {
    setInviteBusy(teamId);
    try {
      if (accept) { const r = await teamsApi.accept(teamId); toast.success(`Joined ${r.data.joined}`); }
      else { await teamsApi.decline(teamId); toast.success('Invitation declined'); }
      setInvites(prev => prev.filter(i => i.team_id !== teamId));
      if (accept) load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not respond'); }
    finally { setInviteBusy(null); }
  };

  const togglePin = async (t, e) => {
    e.stopPropagation();
    setPinBusy(t.id);
    try {
      if (t.pinned) await teamsApi.unpin(t.id); else await teamsApi.pin(t.id);
      setTeams(prev => prev.map(x => x.id === t.id ? { ...x, pinned: !x.pinned } : x));
    } catch { toast.error('Could not update pin'); }
    finally { setPinBusy(null); }
  };

  const setOrgTeam = async (t, orgTeamId, e) => {
    e?.stopPropagation?.();
    try {
      const r = await teamsApi.setOrgTeam(t.id, orgTeamId);
      setTeams(prev => prev.map(x => x.id === t.id ? { ...x, org_team_id: r.data.org_team_id, org_team_name: r.data.org_team_name } : x));
      toast.success(orgTeamId ? `Moved to ${r.data.org_team_name}` : 'Removed from team');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update team'); }
  };

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => teams
    .filter(t => scope === 'all' || (scope === 'pinned' ? t.pinned : t.is_owner))
    .filter(t => !q || (t.name || '').toLowerCase().includes(q) || (t.org_team_name || '').toLowerCase().includes(q) || (t.is_default && 'default'.includes(q))),
    [teams, scope, q]);

  const rest = filtered;
  const hasOrgGroups = teams.some(t => t.org_team_name);
  const topMatch = q && filtered.length
    ? [...filtered].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || (a.name || '').localeCompare(b.name || ''))[0]
    : null;

  // Build ordered groups: org-team groups (if any), then Unassigned / Workspaces.
  const groups = [];
  if (hasOrgGroups) {
    const byOrg = {};
    rest.forEach(t => {
      const key = t.org_team_id || '__none__';
      (byOrg[key] = byOrg[key] || []).push(t);
    });
    orgTeams.forEach(ot => { if (byOrg[ot.id]) groups.push({ id: ot.id, name: ot.name, teams: byOrg[ot.id] }); });
    // Linked to a team not in the picker (e.g. individual seeing an owned team's name)
    Object.keys(byOrg).forEach(k => {
      if (k !== '__none__' && !orgTeams.some(o => o.id === k)) {
        groups.push({ id: k, name: byOrg[k][0].org_team_name || 'Team', teams: byOrg[k] });
      }
    });
    if (byOrg['__none__']) groups.push({ id: '__none__', name: 'Unassigned', teams: byOrg['__none__'] });
  } else if (rest.length) {
    groups.push({ id: 'all', name: 'Workspaces', teams: rest });
  }

  const orderIdx = (id) => { const i = orderIds.indexOf(id); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
  const sortTiles = (arr) => [...arr].sort((a, b) =>
    orderIdx(a.id) - orderIdx(b.id) || (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || (a.name || '').localeCompare(b.name || ''));

  const dropReorder = (targetId) => {
    if (!dragWsId || dragWsId === targetId) { setDragWsId(null); setDropWsId(null); return; }
    // Build from the FULL tile set (not just currently displayed) so an active
    // search/scope filter never wipes hidden workspaces' saved positions.
    const fullSorted = sortTiles(teams).map(t => t.id);
    const next = fullSorted.filter(id => id !== dragWsId);
    const at = next.indexOf(targetId);
    next.splice(at === -1 ? next.length : at, 0, dragWsId);
    setOrderIds(next);
    setDragWsId(null); setDropWsId(null);
    teamsApi.saveOrder(next).catch(() => toast.error('Could not save the new order'));
  };

  const Tile = (t) => {
    const members = t.members || [];
    const shown = members.slice(0, 4);
    const extra = members.length - shown.length;
    const displayName = t.name;
    const canSetTeam = orgTeams.length > 0 && (t.is_owner || role === 'owner');
    return (
      <div key={t.id} onClick={() => navigate(`/w/${t.id}`)} data-testid={`home-ws-tile-${t.id}`}
        role="button" tabIndex={0}
        draggable
        onDragStart={e => { setDragWsId(t.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropWsId !== t.id) setDropWsId(t.id); }}
        onDragLeave={() => { if (dropWsId === t.id) setDropWsId(null); }}
        onDrop={e => { e.preventDefault(); dropReorder(t.id); }}
        onDragEnd={() => { setDragWsId(null); setDropWsId(null); }}
        className={`ws-card${t.pinned ? ' pinned' : ''}`}
        style={dropWsId === t.id && dragWsId && dragWsId !== t.id ? { outline: '2px dashed #6994c6', outlineOffset: 2 } : (dragWsId === t.id ? { opacity: 0.5 } : undefined)}>
        <div className="ws-top">
          <div className={`ws-ic ${t.is_owner ? 'green' : 'blue'}`}>{t.is_default ? <Crown /> : <Users />}</div>
          <div className="ws-id">
            <div className="ws-name" data-testid={`home-ws-name-${t.id}`}>
              <span className="ryg-dot lg" title={RYG_META[teamRYG(t)].label} data-testid={`home-ws-ryg-${t.id}`} style={{ background: RYG_META[teamRYG(t)].color }} />
              <span className="nm-txt">{displayName}</span>
              {t.is_default && <span className="role" style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: '#c79a3f' }} data-testid={`home-ws-default-badge-${t.id}`}>Default</span>}
              {t.is_owner && !t.is_default && <span className="crown" title="You own this workspace"><Crown /></span>}
            </div>
            <div className="ws-meta">
              <span>{members.length} member{members.length === 1 ? '' : 's'}</span>
              {t.org_team_name && <><span>·</span><span className="role">{t.org_team_name}</span></>}
              {!t.is_member && <><span>·</span><span className="pend">View only</span></>}
            </div>
          </div>
          <div className="ws-tools" onClick={e => e.stopPropagation()}>
            <button className="icbtn" onClick={e => togglePin(t, e)} disabled={pinBusy === t.id} data-testid={`home-ws-pin-${t.id}`}
              title={t.pinned ? 'Unpin' : 'Pin to top'} style={{ color: t.pinned ? '#c79a3f' : undefined }}>
              <Pin size={15} fill={t.pinned ? 'currentColor' : 'none'} />
            </button>
            {(canSetTeam || (t.is_owner && !t.is_default)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="icbtn" data-testid={`home-ws-menu-${t.id}`} title="Workspace options" onClick={e => e.stopPropagation()}><MoreVertical size={15} /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white" onClick={e => e.stopPropagation()}>
                  {canSetTeam && (
                    <>
                      <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Belongs to team</DropdownMenuLabel>
                      {orgTeams.map(ot => (
                        <DropdownMenuItem key={ot.id} data-testid={`home-ws-orgteam-${t.id}-${ot.id}`} onClick={e => setOrgTeam(t, ot.id, e)}>
                          <Building2 size={13} className="mr-2" />{ot.name}
                          {t.org_team_id === ot.id && <Check size={13} className="ml-auto" />}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`home-ws-orgteam-${t.id}-none`} onClick={e => setOrgTeam(t, null, e)}>
                        No team{!t.org_team_id && <Check size={13} className="ml-auto" />}
                      </DropdownMenuItem>
                    </>
                  )}
                  {t.is_owner && !t.is_default && (
                    <>
                      {canSetTeam && <DropdownMenuSeparator />}
                      <DropdownMenuItem data-testid={`home-ws-delete-${t.id}`} style={{ color: '#B4332E' }}
                        onClick={e => { e.stopPropagation(); setDeleteName(''); setDeleteTarget(t); }}>
                        <Trash2 size={13} className="mr-2" />Delete workspace…
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <div className="ws-people">
          <div className="avs">
            {shown.map((m, i) => (
              <div key={m.user_id || i} className={`av ${AV_COLORS[i % AV_COLORS.length]}`} title={m.name || m.email}>{initials(m.name || m.email)}</div>
            ))}
            {extra > 0 && <div className="av ink" title={`${extra} more`}>+{extra}</div>}
          </div>
        </div>
        <div className="ws-stats">
          <span className={`ws-stat${t.card_count > 0 ? ' hot' : ''}`}><FileText /><b>{t.card_count || 0}</b> card{t.card_count === 1 ? '' : 's'}</span>
          <span className="ws-stat"><CheckSquare /><b>{t.open_tasks || 0}</b> open</span>
          {t.overdue_tasks > 0 && <span className="ws-stat over"><AlertTriangle /><b>{t.overdue_tasks}</b> overdue</span>}
          {t.last_activity_at && <span className="ws-last">Active {relTime(t.last_activity_at)}</span>}
        </div>
      </div>
    );
  };

  if (loading) return null;

  return (
    <div className="home-ws" data-testid="home-workspaces">
      <div className="sec-row">
        <div className="sh">
          <h3>Workspaces</h3>
          <span className="n" data-testid="home-ws-role">· {ROLE_COPY[role].label}</span>
        </div>
      </div>

      {invites.length > 0 && (
        <div className="home-ws-invites" data-testid="home-ws-invites">
          {invites.map(inv => (
            <div key={inv.team_id} className="ws-invite" data-testid={`home-ws-invite-${inv.team_id}`}>
              <div className="wi-body"><Users size={15} /><span><b>{inv.name}</b> · you've been invited to join</span></div>
              <div className="wi-actions">
                <button className="btn-green" disabled={inviteBusy === inv.team_id} onClick={() => respondInvite(inv.team_id, true)} data-testid={`home-ws-invite-accept-${inv.team_id}`}>Accept</button>
                <button className="btn-ghost" disabled={inviteBusy === inv.team_id} onClick={() => respondInvite(inv.team_id, false)} data-testid={`home-ws-invite-decline-${inv.team_id}`}>Decline</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {role === 'owner' && rollup && (
        <div className="home-health" data-testid="home-ws-rollup" style={{ marginTop: 4 }}>
          <span className="ryg-lbl">Company</span>
          <span className="ryg-chip"><span className="ryg-dot" style={{ background: RYG_META.green.color }} /><b>{rollup.workspace_count}</b> workspaces</span>
          <span className="ryg-chip"><span className="ryg-dot" style={{ background: RYG_META.yellow.color }} /><b>{rollup.open_tasks}</b> open</span>
          <span className="ryg-chip"><span className="ryg-dot" style={{ background: RYG_META.red.color }} /><b>{rollup.overdue_tasks}</b> overdue</span>
        </div>
      )}

      <div className="home-ws-toolbar">
        <div className="tool-search">
          <Search size={15} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search workspaces"
            onKeyDown={e => {
              if (e.key === 'Enter' && topMatch) navigate(`/w/${topMatch.id}`);
              if (e.key === 'Escape') setSearch('');
            }}
            data-testid="home-ws-search" className="w-full border-0 bg-transparent outline-none text-sm" />
          {topMatch && (
            <span data-testid="home-ws-search-hint" className="shrink-0"
              style={{ fontSize: 11, color: 'var(--mute, #8a8f98)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CornerDownLeft size={11} />Open {topMatch.name}
            </span>
          )}
        </div>
        <div className="seg" data-testid="home-ws-scope">
          {[['all', 'All'], ['pinned', 'Pinned'], ['mine', 'Owned']].map(([k, label]) => (
            <button key={k} className={scope === k ? 'on' : ''} data-testid={`home-ws-scope-${k}`} onClick={() => setScope(k)}>{label}</button>
          ))}
          {trash.length > 0 && (
            <button data-testid="home-ws-trash-button" onClick={() => setShowTrash(true)}
              className="inline-flex items-center gap-1.5" title="Recently deleted workspaces">
              <Trash2 size={13} />Trash ({trash.length})
            </button>
          )}
        </div>
        {creating ? (
          <input autoFocus value={newWsName} disabled={wsBusy}
            onChange={e => setNewWsName(e.target.value)}
            onBlur={() => { if (!wsBusy) createWorkspace(newWsName); }}
            onKeyDown={e => {
              if (e.key === 'Enter') createWorkspace(newWsName);
              if (e.key === 'Escape') { setCreating(false); setNewWsName(''); }
            }}
            placeholder="Workspace name" data-testid="home-ws-new-input"
            className="text-sm outline-none"
            style={{ width: 170, height: 34, padding: '0 12px', borderRadius: 10, border: '1px solid rgba(33,46,64,.16)', background: '#fff' }} />
        ) : (
          <button className="btn-green" data-testid="home-ws-new-button" onClick={() => setCreating(true)}
            style={{ whiteSpace: 'nowrap' }}>
            <Plus size={15} />New workspace
          </button>
        )}
      </div>

      {groups.length === 0 && (
        <div className="ac-empty" data-testid="home-ws-empty">
          <div className="eic"><Users size={21} /></div>
          <div className="et">No workspaces{q || scope !== 'all' ? ' match' : ' yet'}</div>
          <div className="es">{q || scope !== 'all' ? 'Try clearing your search or filter' : 'Create a workspace to collaborate with your team'}</div>
        </div>
      )}

      {groups.map(g => {
        const isCollapsed = !!collapsed[g.id];
        const sorted = sortTiles(g.teams);
        const expanded = !!expandedGroups[g.id];
        const shown = expanded ? sorted : sorted.slice(0, CAP);
        const hidden = sorted.length - shown.length;
        return (
          <div key={g.id} className="home-ws-group" data-testid={`home-ws-group-${g.id}`}>
            <button className="home-ws-grouph" onClick={() => setCollapsed(c => ({ ...c, [g.id]: !c[g.id] }))}
              data-testid={`home-ws-group-toggle-${g.id}`}>
              <ChevronDown size={15} style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }} />
              <span className="lab">{g.name}</span>
              <span className="cnt">{sorted.length}</span>
            </button>
            {!isCollapsed && (
              <>
                <div className="ws-grid">{shown.map(Tile)}</div>
                {(hidden > 0 || expanded) && (
                  <div className="flex justify-center mt-3">
                    <button onClick={() => setExpandedGroups(e => ({ ...e, [g.id]: !e[g.id] }))}
                      data-testid={`home-ws-viewall-${g.id}`}
                      className="inline-flex items-center gap-1.5 px-4 h-9 rounded-xl text-sm font-medium border border-border bg-white hover:bg-secondary transition-colors">
                      {expanded ? 'Show less' : `View all (${hidden})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      <InviteOnCreateDialog
        team={inviteTeam}
        open={!!inviteTeam}
        onDone={() => { const id = inviteTeam?.id; setInviteTeam(null); if (id) navigate(`/w/${id}`); }}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!deleting && !o) { setDeleteTarget(null); setDeleteName(''); } }}>
        <DialogContent data-testid="home-ws-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#B4332E' }}>
              <Trash2 size={18} />Delete workspace
            </DialogTitle>
            <DialogDescription>
              This removes <strong>{deleteTarget?.name}</strong> for everyone and moves it to Trash. Action Cards shared here go back to their owners meanwhile. You can restore everything for <strong>30 days</strong> — after that it’s gone for good.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <p className="text-xs text-muted-foreground mb-1.5">Type <strong>{deleteTarget?.name}</strong> to confirm</p>
            <input autoFocus value={deleteName} onChange={e => setDeleteName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && deleteName.trim() === deleteTarget?.name) deleteWorkspace(); }}
              placeholder={deleteTarget?.name} data-testid="home-ws-delete-input"
              className="w-full h-10 px-3 rounded-xl border border-border bg-white text-sm outline-none focus:border-primary" />
          </div>
          <DialogFooter>
            <button onClick={() => { setDeleteTarget(null); setDeleteName(''); }} disabled={deleting} data-testid="home-ws-delete-cancel"
              className="inline-flex items-center justify-center text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors disabled:opacity-60">
              Cancel
            </button>
            <button onClick={deleteWorkspace} disabled={deleting || deleteName.trim() !== deleteTarget?.name} data-testid="home-ws-delete-confirm"
              className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold h-10 px-4 rounded-xl text-white transition-colors disabled:opacity-40"
              style={{ background: '#B4332E' }}>
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}Delete workspace
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showTrash} onOpenChange={setShowTrash}>
        <DialogContent data-testid="home-ws-trash-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 size={18} />Trash</DialogTitle>
            <DialogDescription>Deleted workspaces stay here for 30 days. Restoring brings back the workspace, its folders, tasks and shared cards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[320px] overflow-y-auto py-1">
            {trash.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="home-ws-trash-empty">Trash is empty.</p>
            ) : trash.map(w => (
              <div key={w.id} className="flex items-center gap-3 p-3 rounded-xl border border-border" data-testid={`home-ws-trash-item-${w.id}`}>
                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(180,51,46,.08)', color: '#B4332E' }}><Trash2 size={14} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground truncate">{w.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Deleted {relTime(w.deleted_at)} · {w.card_count} card{w.card_count === 1 ? '' : 's'} · {w.task_count} task{w.task_count === 1 ? '' : 's'} ·{' '}
                    <span style={{ color: w.days_left <= 5 ? '#B4332E' : undefined }}>{w.days_left} day{w.days_left === 1 ? '' : 's'} left</span>
                  </p>
                </div>
                <button onClick={() => restoreWorkspace(w.id)} disabled={restoring === w.id} data-testid={`home-ws-trash-restore-${w.id}`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white shrink-0 disabled:opacity-50"
                  style={{ background: '#5a7d52' }}>
                  {restoring === w.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}Restore
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
